import React, { useCallback, useMemo, useState } from 'react';

import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRenderCellParams } from '@mui/x-data-grid';

import { useSearchRewardsReservations } from '~/hooks/apis/rewards/useRewardsReservations';
import { useFlashHotelCatalog, useRewardsPools } from '~/hooks/apis/rewards/useSocieteReward';

import type { RewardsReservationStatus, SearchRewardsReservationsParams } from '~/services/LuxLoyalty';
import { formatDateLongMonthWithMeridiem } from '~/services/TimeService';

import { toReservationSearchFilters } from './flashReservationFilters';
import { type FlashReservationRow, toFlashReservationRow } from './flashReservationRow';
import { flashRewardsOfferUrl } from './flashRewardsUrl';

const STATUS_OPTIONS: Array<{ value: RewardsReservationStatus; label: string }> = [
  { value: 'RESERVED', label: 'Reserved' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'RELEASED', label: 'Released' },
  { value: 'EXPIRED', label: 'Expired' },
];

const STATUS_CHIP_COLOR: Record<RewardsReservationStatus, 'default' | 'primary' | 'success' | 'warning'> = {
  RESERVED: 'primary',
  CONFIRMED: 'success',
  RELEASED: 'default',
  EXPIRED: 'warning',
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** Loyalty's pools listing returns at most this many per page; it's enough to map current offers. */
const POOL_LOOKUP_LIMIT = 100;

/** Formats an ISO timestamp for the grid, or a dash when absent. */
function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  return formatDateLongMonthWithMeridiem(value) || value;
}

/**
 * Flash Rewards → Reservations: every Société flash-hotel reservation, newest first, filtered by
 * offer, package, order and status. Uses loyalty's reservation search fixed to the hotel_flash
 * catalog; the offer and package filters are sent as loyalty's `itemReference` /
 * `itemSubReference` (see toReservationSearchFilters).
 *
 * Reservations carry no offer id, only a pool id, so the Offer column comes from the pools
 * listing (first page, up to 100 pools). A reservation whose pool isn't in that page shows its
 * pool id instead.
 */
export default function FlashRewardsReservationsPage(): React.JSX.Element {
  const [offerIdInput, setOfferIdInput] = useState('');
  const [packageIdInput, setPackageIdInput] = useState('');
  const [orderIdInput, setOrderIdInput] = useState('');
  const [statusInput, setStatusInput] = useState<RewardsReservationStatus | ''>('');
  const [appliedFilters, setAppliedFilters] = useState<ReturnType<typeof toReservationSearchFilters>>({});
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });

  const catalogQuery = useFlashHotelCatalog();
  const catalogId = catalogQuery.data?.id;
  const poolsQuery = useRewardsPools({ limit: POOL_LOOKUP_LIMIT, offset: 0 });

  const queryParams: SearchRewardsReservationsParams | null = useMemo(
    () =>
      catalogId
        ? {
            ...appliedFilters,
            catalogId,
            limit: paginationModel.pageSize,
            offset: paginationModel.page * paginationModel.pageSize,
            sort: 'createdAt',
            direction: 'desc',
          }
        : null,
    [appliedFilters, catalogId, paginationModel],
  );
  const { data, isFetching, error } = useSearchRewardsReservations(queryParams);

  const offerIdByPoolId = useMemo(() => {
    const map = new Map<string, string>();
    for (const pool of poolsQuery.data?.pools ?? []) {
      if (pool.poolId) map.set(pool.poolId, pool.offerId);
    }
    return map;
  }, [poolsQuery.data]);

  const rows = useMemo(
    () => (data?.reservations ?? []).map((reservation) => toFlashReservationRow(reservation, offerIdByPoolId)),
    [data, offerIdByPoolId],
  );

  const handleSearch = useCallback(() => {
    setAppliedFilters(
      toReservationSearchFilters({
        offerId: offerIdInput,
        packageId: packageIdInput,
        orderId: orderIdInput,
        status: statusInput,
      }),
    );
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [offerIdInput, packageIdInput, orderIdInput, statusInput]);

  const handleClear = useCallback(() => {
    setOfferIdInput('');
    setPackageIdInput('');
    setOrderIdInput('');
    setStatusInput('');
    setAppliedFilters({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const columns = useMemo<Array<GridColDef<FlashReservationRow>>>(
    () => [
      {
        field: 'offerId',
        headerName: 'Offer',
        width: 200,
        sortable: false,
        renderCell: (params: GridRenderCellParams<FlashReservationRow, string | null>) =>
          params.value ? (
            <Link component={RouterLink} to={flashRewardsOfferUrl(params.value)} underline="hover">
              {params.value}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {params.row.poolId ? `Pool ${params.row.poolId}` : '—'}
            </Typography>
          ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams<FlashReservationRow, RewardsReservationStatus>) =>
          params.value ? <Chip label={params.value} size="small" color={STATUS_CHIP_COLOR[params.value]} /> : null,
      },
      {
        field: 'checkIn',
        headerName: 'Check-in',
        width: 120,
        sortable: false,
        valueFormatter: (value) => value ?? '—',
      },
      { field: 'nights', headerName: 'Nights', width: 80, sortable: false, valueFormatter: (value) => value ?? '—' },
      {
        field: 'points',
        headerName: 'Points',
        width: 110,
        sortable: false,
        valueFormatter: (value: number) => value.toLocaleString('en-AU'),
      },
      {
        field: 'cash',
        headerName: 'Co-pay',
        width: 110,
        sortable: false,
        valueGetter: (_value, row) => `${row.currency} ${row.cash}`,
      },
      {
        field: 'orderId',
        headerName: 'Order',
        width: 290,
        sortable: false,
        renderCell: (params: GridRenderCellParams<FlashReservationRow, string>) => (
          <Link
            component={RouterLink}
            to={`/purchases/${params.value}`}
            target="_blank"
            rel="noopener"
            underline="hover"
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: 'reservedAt',
        headerName: 'Reserved',
        width: 190,
        sortable: false,
        valueFormatter: (value: string) => formatTimestamp(value),
      },
      {
        field: 'expiresAt',
        headerName: 'Expires',
        width: 190,
        sortable: false,
        valueFormatter: (value: string) => formatTimestamp(value),
      },
    ],
    [],
  );

  if (catalogQuery.isSuccess && !catalogQuery.data) {
    return (
      <Alert severity="info">No active hotel_flash reward catalog yet, so there are no reservations to show.</Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              label="Offer ID"
              size="small"
              value={offerIdInput}
              onChange={(event) => setOfferIdInput(event.target.value)}
              sx={{ minWidth: 220 }}
            />
            <TextField
              label="Package ID"
              size="small"
              value={packageIdInput}
              onChange={(event) => setPackageIdInput(event.target.value)}
              placeholder="As sent at booking"
              sx={{ minWidth: 220 }}
            />
            <TextField
              label="Order ID"
              size="small"
              value={orderIdInput}
              onChange={(event) => setOrderIdInput(event.target.value)}
              sx={{ minWidth: 300 }}
            />
            <TextField
              select
              label="Status"
              size="small"
              value={statusInput}
              onChange={(event) => setStatusInput(event.target.value as RewardsReservationStatus | '')}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={handleSearch} disabled={isFetching || !catalogId}>
              Search
            </Button>
            <Button variant="text" onClick={handleClear} disabled={isFetching}>
              Clear filters
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {(error || catalogQuery.error) && (
        <Alert severity="error">
          {(error ?? catalogQuery.error) instanceof Error
            ? ((error ?? catalogQuery.error) as Error).message
            : 'Failed to load reservations.'}
        </Alert>
      )}

      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isFetching || catalogQuery.isLoading}
          autoHeight
          paginationMode="server"
          rowCount={data?.total ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
        />
      </Box>
    </Stack>
  );
}
